// เนื้อเรื่องกุ้ง ปู และพวกพ้อง กับหอยและหมึก

import type { GameEvent } from "../../engine/types";

const C = ["CRUSTACEAN"] as const;
const MO = ["MOLLUSC"] as const;
const DECAPOD = ["crustacean.decapod"];
const CEPH = ["mollusc.cephalopod"];
const SNAIL = ["mollusc.gastropod"];
const BIVALVE = ["mollusc.bivalve"];
const HERMIT_CRABS = ["Paguridae", "Diogenidae", "Coenobitidae"];

export const CRUSTACEAN_MOLLUSC_EVENTS: readonly GameEvent[] = [
  // ---------- ทั้งสองกลุ่ม ----------
  {
    id: "trawl_bottom",
    archetypes: ["CRUSTACEAN", "MOLLUSC", "ECHINODERM"],
    stageTags: ["young", "adult", "old"],
    requires: { sessile: false, habitat: ["sea"] },
    weight: 2,
    threatenedWeight: 2,
    text: "อวนลากหน้าดินครูดผ่านพื้นทะเล ตะกอนฟุ้งขึ้นมาจนมองไม่เห็นอะไร",
    choices: [
      {
        label: "ซุกตัวแนบพื้นให้ต่ำที่สุด",
        outcomes: [
          { chance: 0.6, text: "ปากอวนครูดผ่านเหนือตัวคุณไปอย่างเฉียดฉิว", effects: { safety: -5 } },
          { chance: 0.3, text: "โซ่ถ่วงอวนลากผ่านตัวคุณ คุณบาดเจ็บแต่ไม่ติดอวน", effects: { health: -25, cause: "human" } },
          { chance: 0.1, text: "อวนกวาดคุณขึ้นไปพร้อมกับทุกอย่างบนพื้นทะเล", effects: { death: "human" } },
        ],
      },
      {
        label: "หนีออกนอกแนวอวน",
        outcomes: [
          { chance: 0.65, text: "คุณหนีพ้นแนวอวนได้ทัน", effects: { fullness: -10 } },
          { chance: 0.35, text: "อวนกว้างเกินกว่าจะหนีพ้น คุณถูกลากขึ้นเรือ", effects: { death: "human" } },
        ],
      },
    ],
  },

  // ---------- กุ้งปูทั่วไป ----------
  {
    id: "crust_larva_drift",
    archetypes: C,
    stages: ["larva"],
    weight: 8,
    text: "คุณเป็นตัวอ่อนตัวใสมีหนามยาว ล่องลอยปะปนกับแพลงก์ตอนนับล้าน",
    fact: "สัตว์ตัวจิ๋วในทะเลจำนวนมหาศาลว่ายขึ้นผิวน้ำตอนกลางคืน และดำลงที่ลึกตอนกลางวันทุกวัน นับเป็นการอพยพที่ใหญ่ที่สุดในโลก",
    choices: [
      { label: "ขึ้นผิวน้ำเฉพาะตอนกลางคืน", outcomes: [{ chance: 1, text: "กลางคืนนักล่ามองไม่เห็น คุณกินแพลงก์ตอนพืชจนอิ่ม", effects: { fullness: 30, safety: 5 } }] },
      {
        label: "อยู่ผิวน้ำตลอดเวลา",
        outcomes: [
          { chance: 0.6, text: "ผิวน้ำมีอาหารมากที่สุด คุณโตเร็ว", effects: { fullness: 35 } },
          { chance: 0.4, text: "ฝูงปลากะตักกวาดกินแพลงก์ตอนผ่านมา คุณรอดมาอย่างหวุดหวิด", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "crust_larva_current",
    archetypes: C,
    stages: ["larva"],
    weight: 4,
    text: "กระแสน้ำแรงกำลังพาฝูงแพลงก์ตอนที่คุณอยู่ออกห่างจากฝั่ง",
    choices: [
      {
        label: "ปล่อยให้กระแสน้ำพาไป",
        outcomes: [
          { chance: 0.7, text: "คุณถูกพาไปไกลหลายสิบกิโลเมตร แล้ววนกลับมาใกล้ชายฝั่งอีกครั้ง", effects: { move: "far", fullness: 10 } },
          { chance: 0.3, text: "กระแสน้ำพาคุณออกไปกลางทะเลที่แทบไม่มีอาหาร", effects: { move: "far", fullness: -20, health: -10, cause: "starvation" } },
        ],
      },
      { label: "ดำลงไปหาน้ำที่นิ่งกว่า", outcomes: [{ chance: 1, text: "ข้างล่างน้ำนิ่งแต่อาหารน้อย คุณค่อยๆ ลอยกลับเข้าหาฝั่ง", effects: { fullness: 5, safety: 5 } }] },
    ],
  },
  {
    id: "crust_molt",
    archetypes: C,
    stages: ["juvenile", "adult", "elder"],
    weight: 6,
    text: "เปลือกของคุณคับจนอึดอัด ถึงเวลาลอกคราบแล้ว",
    fact: "ปูนิ่มคือปูที่เพิ่งลอกคราบ และเปลือกใหม่ยังไม่แข็ง",
    choices: [
      {
        label: "ซ่อนในซอกหินจนเปลือกใหม่แข็ง",
        outcomes: [
          { chance: 0.9, text: "คุณดูดน้ำเข้าตัวจนพองแล้วรอให้เปลือกแข็ง ตัวคุณใหญ่ขึ้นกว่าเดิม", effects: { health: 10, fullness: -10 } },
          { chance: 0.1, text: "ขาข้างหนึ่งติดอยู่ในคราบเก่าจนหลุดไป", effects: { health: -25, cause: "accident" } },
        ],
      },
      {
        label: "รีบออกไปหาอาหารทั้งที่ตัวยังนิ่ม",
        outcomes: [
          { chance: 0.5, text: "คุณได้อาหารก่อนตัวอื่น", effects: { fullness: 25 } },
          { chance: 0.5, text: "ปลาเห็นตัวนิ่มๆ ของคุณและพุ่งเข้ามางับ", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  },

  // ---------- กุ้งและปู ----------
  {
    id: "crust_egg_carried",
    opening: true,
    archetypes: C,
    stages: ["egg"],
    requires: { subgroups: DECAPOD },
    weight: 10,
    text: "คุณเป็นไข่เม็ดจิ๋วหนึ่งในหลายพันฟองที่เกาะอยู่ใต้ท้องแม่ แม่ขยับขาพัดน้ำผ่านไข่ไม่หยุด",
    fact: "ปูและกุ้งหลายชนิดเก็บไข่ไว้ใต้ท้องแม่ ใช้ขาว่ายน้ำพัดน้ำให้ไข่ได้ออกซิเจนจนฟัก",
    choices: [{ label: "รอวันฟัก", outcomes: [{ chance: 1, text: "น้ำสะอาดไหลผ่านตลอดเวลา ไข่ของคุณเติบโตอย่างแข็งแรง", effects: { health: 5 } }] }],
  },
  {
    id: "crab_claw_drop",
    archetypes: C,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: DECAPOD },
    weight: 4,
    text: "นกยางจิกโดนก้ามของคุณ และไม่ยอมปล่อย",
    fact: "ปูและกุ้งหลายชนิดสลัดขาหรือก้ามทิ้งได้เมื่อถูกจับ แล้วค่อยๆ งอกใหม่ในการลอกคราบครั้งถัดๆ ไป",
    choices: [
      { label: "สลัดก้ามทิ้งแล้ววิ่งหนี", outcomes: [{ chance: 1, text: "คุณสลัดก้ามทิ้งแล้ววิ่งลงรู ก้ามใหม่จะค่อยๆ งอกขึ้นมาในการลอกคราบครั้งต่อไป", effects: { health: -10, safety: 5 } }] },
      {
        label: "ดึงสู้สุดแรง",
        outcomes: [
          { chance: 0.4, text: "คุณหนีบปากนกจนมันสะดุ้งปล่อย", effects: { health: -10 } },
          { chance: 0.4, text: "นกเหวี่ยงคุณกระแทกพื้นก่อนจะทำหล่น", effects: { health: -30, cause: "predator" } },
          { chance: 0.2, text: "นกกลืนคุณลงไปทั้งตัว", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "hermit_shell",
    archetypes: C,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: DECAPOD, families: HERMIT_CRABS },
    weight: 8,
    text: "เปลือกหอยที่คุณใช้เป็นบ้านคับเกินไปแล้ว",
    fact: "ปูเสฉวนบางทีมาต่อแถวเรียงตามขนาดข้างเปลือกหอยว่าง แล้วผลัดกันย้ายเข้าเปลือกที่ใหญ่ขึ้นทีละตัว",
    choices: [
      { label: "รอต่อแถวเปลือกที่ตัวอื่นทิ้ง", outcomes: [{ chance: 1, text: "ปูตัวใหญ่ย้ายเข้าเปลือกใหม่ คุณได้เปลือกเก่าของมันที่พอดีตัวพอดี", effects: { safety: 15 } }] },
      {
        label: "แย่งเปลือกจากตัวอื่น",
        outcomes: [
          { chance: 0.5, text: "คุณดึงอีกตัวออกจากเปลือกได้สำเร็จ", effects: { safety: 15, fullness: -5 } },
          { chance: 0.5, text: "อีกตัวเกาะเปลือกแน่นกว่า และหนีบคุณกลับ", effects: { health: -15, cause: "accident" } },
        ],
      },
      { label: "ใช้ฝาขวดพลาสติกไปก่อน", outcomes: [{ chance: 1, text: "ฝาพลาสติกเบาแต่บาง กันอะไรไม่ค่อยได้", effects: { safety: -10 } }] },
    ],
  },
  {
    id: "land_crab_migration",
    archetypes: C,
    stages: ["adult"],
    requires: { subgroups: DECAPOD, families: ["Gecarcinidae"] },
    weight: 6,
    text: "ฝนแรกของฤดูมาถึงแล้ว ปูนับไม่ถ้วนพากันเดินออกจากป่าลงสู่ทะเล",
    fact: "ปูแดงบนเกาะคริสต์มาสหลายสิบล้านตัวเดินจากป่าลงทะเลพร้อมกันทุกปี เพื่อผสมพันธุ์และปล่อยไข่ลงทะเล",
    choices: [
      {
        label: "เดินตามฝูงข้ามถนนไป",
        outcomes: [
          { chance: 0.7, text: "คุณไปถึงชายหาดและเจอคู่", effects: { move: "near", addFlags: ["has_mate"], fullness: -15 } },
          { chance: 0.2, text: "รถคันหนึ่งทับขาคุณ แต่คุณยังเดินต่อไปได้", effects: { health: -30, cause: "human" } },
          { chance: 0.1, text: "รถวิ่งผ่านมาหลายคันเกินกว่าจะหลบทัน", effects: { death: "human" } },
        ],
      },
      { label: "เดินอ้อมไปทางลำธาร", outcomes: [{ chance: 1, text: "ทางไกลกว่าแต่ปลอดภัย คุณไปถึงทะเลช้ากว่าตัวอื่น", effects: { fullness: -20 } }] },
    ],
  },
  {
    id: "crab_wave",
    archetypes: C,
    stages: ["adult"],
    requires: { subgroups: DECAPOD, notFlags: ["has_mate"] },
    weight: 12,
    text: "ช่วงนี้มีอีกตัวคอยตามคุณไปทุกที่ และยกก้ามขึ้นโบกให้ดูเป็นระยะ",
    fact: "ปูก้ามดาบตัวผู้มีก้ามข้างหนึ่งใหญ่มาก ใช้โบกเรียกตัวเมียและข่มคู่แข่ง",
    choices: [
      {
        label: "ตอบรับ",
        outcomes: [
          { chance: 0.75, text: "พวกคุณจับคู่กันได้", effects: { addFlags: ["has_mate"] } },
          { chance: 0.25, text: "คู่แข่งตัวใหญ่เข้ามาแย่ง คุณโดนหนีบ", effects: { health: -15, cause: "accident" } },
        ],
      },
      { label: "เดินหนีลงรู", outcomes: [{ chance: 1, text: "คุณมุดลงรูไปคนเดียว" }] },
    ],
  },
  {
    id: "crust_berried",
    archetypes: C,
    stages: ["adult"],
    requires: { subgroups: DECAPOD, flags: ["has_mate"] },
    weight: 25,
    text: "ไข่ของคุณได้รับการผสมแล้ว ถึงเวลาดูแลมันจนฟัก",
    choices: [
      { label: "อุ้มไข่ไว้ใต้ท้อง", outcomes: [{ chance: 1, text: "ไข่ {n} ฟองเกาะใต้ท้องคุณเป็นพวง คุณพัดน้ำให้มันทุกวันจนฟักเป็นตัวอ่อน", effects: { offspring: "clutch", fullness: -15, removeFlags: ["has_mate"], addFlags: ["parent"] } }] },
      {
        label: "ไปปล่อยตัวอ่อนในที่น้ำไหลแรง",
        outcomes: [
          { chance: 0.8, text: "กระแสน้ำพาตัวอ่อน {n} ตัวกระจายออกไปไกล", effects: { offspring: "clutch", fullness: -20, removeFlags: ["has_mate"], addFlags: ["parent"] } },
          { chance: 0.2, text: "คลื่นซัดคุณกระแทกหิน แต่ตัวอ่อน {n} ตัวก็ออกไปได้", effects: { offspring: "clutch", health: -20, removeFlags: ["has_mate"], addFlags: ["parent"], cause: "accident" } },
        ],
      },
    ],
  },
  {
    id: "crab_trap",
    archetypes: C,
    stages: ["adult", "elder"],
    requires: { subgroups: DECAPOD },
    weight: 2.5,
    threatenedWeight: 2,
    text: "กลิ่นปลาเน่าลอยมาจากลอบไม้ไผ่ที่วางอยู่ใต้น้ำ",
    choices: [
      {
        label: "เข้าไปกินเหยื่อ",
        outcomes: [
          { chance: 0.5, text: "คุณกินเหยื่อแล้วหาทางออกเจอก่อนรุ่งเช้า", effects: { fullness: 30 } },
          { chance: 0.3, text: "ชาวประมงยกลอบขึ้นมา แต่คุณตัวเล็กเกินไป เขาจึงโยนคุณคืนลงน้ำ", effects: { health: -10, fullness: 20 } },
          { chance: 0.2, text: "ทางออกของลอบเป็นกรวยแคบ คุณหาทางออกไม่เจอจนชาวประมงมายกขึ้นเรือ", effects: { death: "human" } },
        ],
      },
      { label: "ไม่สนกลิ่นนั้น", outcomes: [{ chance: 1, text: "คุณหาซากปลาที่พื้นกินแทน", effects: { fullness: 15 } }] },
    ],
  },

  // ---------- สัตว์พวกกุ้งปูอื่นๆ ----------
  {
    id: "copepod_tiny",
    archetypes: C,
    stages: ["larva", "juvenile", "adult", "elder"],
    requires: { subgroups: ["crustacean.other"] },
    weight: 6,
    text: "คุณตัวเล็กเท่าเม็ดทราย ว่ายกระตุกไปมาด้วยหนวดคู่ยาว",
    fact: "โคพีพอดเป็นสัตว์ตัวจิ๋วที่มีจำนวนมากที่สุดกลุ่มหนึ่งในน้ำ และเป็นอาหารสำคัญของลูกปลาเกือบทุกชนิด",
    choices: [
      { label: "กรองกินแพลงก์ตอนพืช", outcomes: [{ chance: 1, text: "น้ำแถวนี้เขียวไปด้วยแพลงก์ตอน คุณกินจนอิ่ม", effects: { fullness: 30 } }] },
      {
        label: "ดีดตัวหนีลูกปลาที่ว่ายผ่าน",
        outcomes: [
          { chance: 0.75, text: "คุณดีดตัวได้ไกลกว่าความยาวลำตัวหลายเท่าในพริบตา", effects: { safety: 10 } },
          { chance: 0.25, text: "ลูกปลาเร็วกว่า คุณถูกงับไปครึ่งหนึ่งของหนวด", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "isopod_roll",
    archetypes: C,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["crustacean.other"], families: ["Armadillidiidae"] },
    weight: 7,
    text: "จู่ๆ แสงแดดก็สาดลงมา มีคนยกกระถางต้นไม้ที่คุณซ่อนอยู่ข้างใต้ขึ้น",
    fact: "ไอโซพอดบกที่ม้วนตัวกลมได้ไม่ใช่แมลง แต่เป็นสัตว์พวกกุ้งปูที่ขึ้นมาอยู่บนบก จึงยังต้องอยู่ในที่ชื้น",
    choices: [
      { label: "ม้วนตัวเป็นลูกบอล", outcomes: [{ chance: 1, text: "คนคนนั้นเขี่ยคุณเล่นแล้ววางกระถางกลับที่เดิม", effects: { safety: 10 } }] },
      {
        label: "รีบคลานหาที่มืด",
        outcomes: [
          { chance: 0.7, text: "คุณมุดเข้าใต้กองใบไม้ชื้นได้ทัน", effects: { safety: 5, fullness: 10 } },
          { chance: 0.3, text: "แดดร้อนทำให้ตัวคุณแห้งระหว่างทาง", effects: { health: -15, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "barnacle_feed",
    archetypes: C,
    stages: ["adult", "elder"],
    requires: { subgroups: ["crustacean.other"], families: ["Balanidae"] },
    weight: 7,
    text: "น้ำขึ้นท่วมโขดหินที่คุณยึดติดอยู่มาทั้งชีวิต",
    fact: "เพรียงหินเป็นสัตว์พวกกุ้งปู ไม่ใช่หอย ตัวเต็มวัยยึดหัวติดกับหิน แล้วใช้ขาที่มีขนยื่นออกมาพัดจับอาหาร",
    choices: [
      { label: "เปิดฝายื่นขาออกมาพัดจับอาหาร", outcomes: [{ chance: 1, text: "ขาที่มีขนกวาดแพลงก์ตอนเข้าปากไม่หยุด", effects: { fullness: 35 } }] },
      { label: "ปิดฝารอให้หอยฝาเดียวคลานผ่านไปก่อน", outcomes: [{ chance: 1, text: "หอยกินเนื้อคลานผ่านไปโดยเจาะเปลือกคุณไม่สำเร็จ", effects: { safety: 10, fullness: 10 } }] },
    ],
  },
  {
    id: "krill_whale",
    archetypes: C,
    stages: ["larva", "juvenile", "adult"],
    requires: { subgroups: ["crustacean.other"], families: ["Euphausiidae"] },
    weight: 6,
    text: "ทะเลรอบตัวมืดลงกะทันหัน ปากขนาดมหึมาของวาฬกำลังอ้ากว้างเข้ามาหาฝูงของคุณ",
    fact: "เคยแอนตาร์กติกเป็นอาหารหลักของวาฬ แมวน้ำ นกเพนกวิน และปลาอีกจำนวนมาก",
    choices: [
      {
        label: "ดีดตัวถอยหลังหนีสุดแรง",
        outcomes: [
          { chance: 0.6, text: "คุณหลุดออกจากขอบปากวาฬได้ทัน ขณะที่ฝูงครึ่งหนึ่งหายเข้าไป", effects: { safety: -10 } },
          { chance: 0.4, text: "คุณถูกกลืนลงไปพร้อมเพื่อนอีกนับหมื่น", effects: { death: "predator" } },
        ],
      },
      {
        label: "ดำลงที่ลึก",
        outcomes: [
          { chance: 0.7, text: "คุณดำลงไปพ้นระยะ วาฬกลืนแต่ฝูงข้างบน", effects: { fullness: -10 } },
          { chance: 0.3, text: "วาฬดำตามลงมา", effects: { death: "predator" } },
        ],
      },
    ],
  },

  // ---------- หมึก ----------
  {
    id: "ceph_egg",
    archetypes: MO,
    stages: ["egg"],
    requires: { subgroups: CEPH },
    weight: 6,
    text: "คุณอยู่ในไข่ใสที่ห้อยเป็นพวงใต้โขดหิน มองเห็นตัวเองกำลังเติบโตอยู่ข้างใน",
    fact: "แม่หมึกสายหลายชนิดเฝ้าพวงไข่ พ่นน้ำให้ไข่ได้ออกซิเจนและปัดสิ่งสกปรกออกจนกว่าลูกจะฟัก",
    choices: [{ label: "รอวันฟัก", outcomes: [{ chance: 1, text: "ตาโตๆ ของคุณเริ่มมีสีเข้มขึ้นทุกวัน", effects: { health: 5 } }] }],
  },
  {
    id: "ceph_paralarva_drift",
    archetypes: MO,
    stages: ["paralarva"],
    weight: 8,
    text: "คุณล่องลอยปะปนกับแพลงก์ตอน ตาโตของคุณคอยมองหาเหยื่อตัวจิ๋ว",
    choices: [
      {
        label: "พุ่งจับตัวอ่อนกุ้งปู",
        outcomes: [
          { chance: 0.8, text: "หนวดเล็กๆ ของคุณจับเหยื่อได้แม่นยำ", effects: { fullness: 30 } },
          { chance: 0.2, text: "เหยื่อหลุดมือไปทุกครั้ง", effects: { fullness: 5 } },
        ],
      },
      { label: "ลอยนิ่งๆ ทำตัวใสเหมือนแพลงก์ตอน", outcomes: [{ chance: 1, text: "ไม่มีใครสังเกตเห็นคุณ และมีอาหารลอยมาใกล้บ้าง", effects: { safety: 10, fullness: 10 } }] },
    ],
  },
  {
    id: "ceph_paralarva_jet",
    archetypes: MO,
    stages: ["paralarva"],
    weight: 5,
    text: "ปลาตัวเล็กตัวหนึ่งว่ายตรงมาหาคุณ",
    fact: "ลูกหมึกพ่นหมึกได้ตั้งแต่ฟักออกจากไข่",
    choices: [
      {
        label: "พ่นน้ำพุ่งตัวหนี",
        outcomes: [
          { chance: 0.7, text: "คุณพุ่งหนีไปได้ไกล", effects: { safety: 5, fullness: -5 } },
          { chance: 0.3, text: "ปลาเร็วกว่า งับโดนหนวดคุณไปเส้นหนึ่ง", effects: { health: -25, cause: "predator" } },
        ],
      },
      {
        label: "พ่นหมึกจิ๋วแล้วหนี",
        outcomes: [
          { chance: 0.85, text: "ปลางับก้อนหมึกแทนตัวคุณ", effects: { safety: 10 } },
          { chance: 0.15, text: "ปลาไม่หลงกล", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "ceph_camouflage",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: CEPH },
    weight: 8,
    text: "ฉลามตัวหนึ่งว่ายเลียบพื้นทะเลเข้ามาทางนี้",
    fact: "หมึกเปลี่ยนสีผิวได้ในเสี้ยววินาที ด้วยถุงสีจำนวนมากใต้ผิวหนังที่ควบคุมด้วยกล้ามเนื้อและเส้นประสาท",
    choices: [
      {
        label: "เปลี่ยนสีกลืนไปกับโขดหิน",
        outcomes: [
          { chance: 0.85, text: "ผิวของคุณกลายเป็นลายตะปุ่มตะป่ำเหมือนหิน ฉลามว่ายผ่านไปโดยไม่เห็น", effects: { safety: 10 } },
          { chance: 0.15, text: "ฉลามได้กลิ่นคุณ และงับหนวดไปเส้นหนึ่ง", effects: { health: -25, addFlags: ["injured"], cause: "predator" } },
        ],
      },
      {
        label: "พ่นหมึกแล้วพุ่งหนี",
        outcomes: [
          { chance: 0.8, text: "ฉลามงับก้อนหมึกที่มีรูปร่างคล้ายตัวคุณ แล้วคุณก็หายไป", effects: { safety: 5, fullness: -5 } },
          { chance: 0.2, text: "ฉลามว่ายทะลุกลุ่มหมึกตามมา", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "ceph_hunt",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: CEPH },
    weight: 8,
    text: "ปูตัวหนึ่งเดินอยู่บนพื้นทราย ไม่รู้ตัวว่ามีคุณอยู่ใกล้ๆ",
    fact: "หมึกสายมีเซลล์ประสาทราว 500 ล้านเซลล์ ราวสองในสามอยู่ในหนวด หนวดแต่ละเส้นจึงสัมผัสและรับรสได้เอง",
    choices: [
      {
        label: "ย่องเข้าไปจากด้านหลัง",
        outcomes: [
          { chance: 0.8, text: "คุณครอบปูไว้ใต้พังผืดระหว่างหนวด แล้วกินมันอย่างสบาย", effects: { fullness: 35 } },
          { chance: 0.2, text: "ปูหันมาหนีบหนวดของคุณ", effects: { fullness: 10, health: -10, cause: "accident" } },
        ],
      },
      {
        label: "พุ่งตะครุบทันที",
        outcomes: [
          { chance: 0.6, text: "ตะครุบได้ทันก่อนมันจะวิ่งหนี", effects: { fullness: 35 } },
          { chance: 0.4, text: "ปูวิ่งหนีเข้าซอกหินไปได้", effects: { fullness: 5 } },
        ],
      },
    ],
  },
  {
    id: "ceph_coconut",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: CEPH, families: ["Octopodidae"] },
    weight: 4,
    text: "คุณเจอกะลามะพร้าวสองซีกจมอยู่บนพื้นทรายโล่งๆ",
    fact: "หมึกสายบางชนิดพกกะลามะพร้าวไปด้วย แล้วประกอบเป็นที่หลบภัยเมื่อต้องการ นับเป็นการใช้เครื่องมือของสัตว์ไม่มีกระดูกสันหลัง",
    choices: [
      { label: "พกไปด้วยเป็นบ้านเคลื่อนที่", outcomes: [{ chance: 1, text: "พอมีภัย คุณก็ประกบกะลาเข้าหากันแล้วหลบอยู่ข้างใน", effects: { safety: 20, fullness: -5 } }] },
      { label: "ปล่อยไว้ แล้วไปล่าปูต่อ", outcomes: [{ chance: 1, text: "คุณเจอปูตัวเล็กซ่อนอยู่ใต้กะลานั่นเอง", effects: { fullness: 20 } }] },
    ],
  },
  {
    id: "ceph_threat",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: CEPH, families: ["Octopodidae"] },
    weight: 3,
    text: "ปลาไหลมอเรย์ยื่นหัวเข้ามาในโพรงของคุณ",
    fact: "หมึกสายวงน้ำเงินโชว์วงสีฟ้าสว่างเมื่อถูกคุกคาม และมีพิษเทโทรโดท็อกซินที่ร้ายแรงถึงชีวิตมนุษย์",
    choices: [
      {
        label: "เปลี่ยนสีสลับมืดสว่างขู่",
        outcomes: [
          { chance: 0.7, text: "ปลาไหลชะงัก แล้วถอยออกไป", effects: { safety: 5 } },
          { chance: 0.3, text: "ปลาไหลไม่กลัว และงับโดนหนวดคุณ", effects: { health: -20, cause: "predator" } },
        ],
      },
      { label: "บีบตัวหนีออกทางรูเล็กด้านหลัง", outcomes: [{ chance: 1, text: "ร่างกายที่ไม่มีกระดูกของคุณลอดรูที่เล็กกว่าหัวตัวเองออกไปได้", effects: { safety: 10, fullness: -5 } }] },
    ],
  },
  {
    id: "ceph_squid_light",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: CEPH },
    weight: 2.5,
    threatenedWeight: 2,
    text: "แสงไฟสว่างจ้าจากเรือประมงส่องลงมาในทะเลยามค่ำ ฝูงปลาเล็กแห่กันขึ้นไปรอบแสงนั้น",
    fact: "เรือไดหมึกใช้ไฟสว่างล่อหมึกให้ขึ้นมาใกล้ผิวน้ำตอนกลางคืน",
    choices: [
      {
        label: "ขึ้นไปล่าปลาใต้แสงไฟ",
        outcomes: [
          { chance: 0.5, text: "ปลาเล็กเยอะมาก คุณกินจนอิ่มแล้วรีบดำลง", effects: { fullness: 35 } },
          { chance: 0.3, text: "เบ็ดไดหมึกเกี่ยวหนวดคุณ คุณดิ้นจนหลุดแต่บาดเจ็บ", effects: { health: -25, cause: "human" } },
          { chance: 0.2, text: "เบ็ดไดหมึกเกี่ยวคุณขึ้นเรือ", effects: { death: "human" } },
        ],
      },
      { label: "อยู่ในที่มืดต่อไป", outcomes: [{ chance: 1, text: "คุณล่าปลาที่หลงลงมาจากแสงไฟแทน", effects: { fullness: 15 } }] },
    ],
  },
  {
    id: "ceph_courtship",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: CEPH, notFlags: ["has_mate", "spent"] },
    weight: 10,
    text: "อีกตัวหนึ่งเปลี่ยนสีผิวเป็นลวดลายระยิบระยับอยู่ตรงหน้าคุณ",
    fact: "หมึกตัวผู้ใช้หนวดพิเศษเส้นหนึ่งส่งถุงสเปิร์มให้ตัวเมีย",
    choices: [
      {
        label: "เปลี่ยนสีตอบ",
        outcomes: [
          { chance: 0.8, text: "ลวดลายของพวกคุณประสานกัน คุณได้คู่แล้ว", effects: { addFlags: ["has_mate"] } },
          { chance: 0.2, text: "คู่แข่งพุ่งเข้ามาไล่ คุณโดนหนวดฟาด", effects: { health: -10, cause: "accident" } },
        ],
      },
      { label: "ว่ายจากไป", outcomes: [{ chance: 1, text: "คุณยังไม่พร้อม" }] },
    ],
  },
  {
    id: "ceph_lay",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: CEPH, flags: ["has_mate"] },
    weight: 30,
    text: "ถึงเวลาวางไข่ ซึ่งสำหรับหมึกส่วนใหญ่คือครั้งเดียวในชีวิต",
    choices: [
      { label: "วางไข่ในโพรงแล้วเฝ้าไว้", outcomes: [{ chance: 1, text: "คุณร้อยไข่ {n} ฟองเป็นพวงห้อยจากเพดานโพรง แล้วคอยพ่นน้ำให้ไข่ทุกวัน", effects: { offspring: "clutch", removeFlags: ["has_mate"], addFlags: ["parent", "spent"] } }] },
      { label: "ติดไข่ไว้ใต้โขดหินแล้วจากไป", outcomes: [{ chance: 1, text: "คุณติดพวงไข่ {n} ฟองไว้ใต้โขดหิน ร่างกายเริ่มอ่อนแรงลงอย่างประหลาด", effects: { offspring: "clutch", removeFlags: ["has_mate"], addFlags: ["parent", "spent"] } }] },
    ],
  },
  {
    id: "ceph_final",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: CEPH, flags: ["spent"] },
    weight: 500,
    text: "ผิวของคุณเริ่มซีดจาง คุณไม่รู้สึกหิวอีกแล้ว",
    fact: "หมึกส่วนใหญ่มีชีวิตเพียงปีหรือสองปี และตายหลังวางไข่ครั้งเดียว แม่หมึกสายจะอดอาหารเฝ้าไข่จนลูกฟักแล้วจึงตาย",
    choices: [
      { label: "เฝ้าไข่จนถึงวินาทีสุดท้าย", outcomes: [{ chance: 1, text: "ลูกๆ ฟักออกมาว่ายจากไปทีละตัว คุณมองดูจนตัวสุดท้าย แล้วหลับตาลง", effects: { death: "sacrifice" } }] },
      { label: "ล่องลอยออกไปในทะเลเป็นครั้งสุดท้าย", outcomes: [{ chance: 1, text: "คุณลอยออกไปในทะเลกว้าง และจากไปอย่างสงบ", effects: { death: "old_age" } }] },
    ],
  },

  // ---------- หอยฝาเดียวและทาก ----------
  {
    id: "snail_retreat",
    archetypes: MO,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: SNAIL },
    weight: 7,
    text: "นกตัวหนึ่งกระโดดเข้ามาใกล้ และเอียงคอมองคุณ",
    choices: [
      {
        label: "หดตัวเข้าเปลือก",
        outcomes: [
          { chance: 0.7, text: "นกเคาะเปลือกคุณสองสามที แล้วบินจากไป", effects: { safety: 5 } },
          { chance: 0.3, text: "นกคาบคุณไปฟาดกับก้อนหินจนเปลือกร้าว", effects: { health: -30, cause: "predator" } },
        ],
      },
      {
        label: "คลานหนีเข้าพุ่มไม้",
        outcomes: [
          { chance: 0.4, text: "นกไม่สนใจคุณ มันกำลังไล่จับแมลง", effects: { safety: 5 } },
          { chance: 0.6, text: "คุณช้าเกินไป นกจิกโดนเนื้อนิ่มๆ", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "snail_radula",
    archetypes: MO,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: SNAIL },
    weight: 6,
    text: "สาหร่ายและตะไคร่ขึ้นเป็นฟิล์มบางๆ อยู่ตรงหน้า",
    fact: "หอยฝาเดียวและทากมีลิ้นพิเศษที่มีฟันเล็กๆ เรียงกันนับพันซี่ เรียกว่าแรดูลา",
    choices: [
      { label: "ขูดกินด้วยลิ้น", outcomes: [{ chance: 1, text: "ลิ้นที่มีฟันนับพันขูดตะไคร่เข้าปากอย่างไม่หยุด", effects: { fullness: 30 } }] },
      { label: "กินแค่พอดีแล้วหลบ", outcomes: [{ chance: 1, text: "คุณกินพอประมาณแล้วกลับไปหลบในที่ร่ม", effects: { fullness: 20, safety: 5 } }] },
    ],
  },
  {
    id: "snail_rain",
    archetypes: MO,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: SNAIL, habitat: ["forest", "grassland", "urban"] },
    weight: 5,
    text: "ฝนเพิ่งหยุด ใบไม้ทุกใบยังเปียกฉ่ำ",
    choices: [
      { label: "ออกไปกินใบไม้อ่อน", outcomes: [{ chance: 1, text: "ใบอ่อนชุ่มน้ำ คุณกินจนอิ่ม", effects: { fullness: 35 } }] },
      {
        label: "คลานขึ้นกำแพงไปหาที่ใหม่",
        outcomes: [
          { chance: 0.6, text: "อีกฝั่งกำแพงเป็นสวนผักที่เต็มไปด้วยใบไม้อ่อน", effects: { move: "near", fullness: 30 } },
          { chance: 0.4, text: "แดดออกก่อนคุณกลับถึงที่ร่ม ตัวคุณเริ่มแห้ง", effects: { health: -15, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "snail_estivate",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: SNAIL, habitat: ["forest", "grassland", "urban"] },
    weight: 4,
    text: "ฝนไม่ตกมาหลายสัปดาห์แล้ว ทุกอย่างรอบตัวแห้งกรอบ",
    fact: "หอยทากบกปิดปากเปลือกด้วยเมือกแห้ง แล้วจำศีลช่วงแล้งได้นานหลายเดือน",
    choices: [
      { label: "ปิดปากเปลือกด้วยเมือกแล้วจำศีล", outcomes: [{ chance: 1, text: "คุณหลับยาวจนฝนกลับมา ร่างกายแทบไม่ใช้พลังงานเลย", effects: { fullness: 10, safety: 10 } }] },
      {
        label: "ออกหาที่ชื้นต่อไป",
        outcomes: [
          { chance: 0.4, text: "คุณเจอร่องน้ำที่ยังชื้นอยู่", effects: { fullness: 20 } },
          { chance: 0.6, text: "แดดร้อนจัด ตัวคุณเสียน้ำไปมาก", effects: { health: -25, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "snail_hermaphrodite",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: SNAIL, notFlags: ["has_mate"] },
    weight: 12,
    text: "อีกตัวหนึ่งคลานตามรอยเมือกของคุณมาจนทัน",
    fact: "หอยทากบกส่วนใหญ่มีทั้งสองเพศในตัวเดียว เมื่อผสมพันธุ์กันจึงออกไข่ได้ทั้งคู่",
    choices: [
      {
        label: "ทักทายด้วยหนวด",
        outcomes: [
          { chance: 0.85, text: "พวกคุณวนรอบกันอยู่นาน แล้วผสมพันธุ์กัน", effects: { addFlags: ["has_mate"] } },
          { chance: 0.15, text: "อีกตัวเปลี่ยนใจคลานจากไป" },
        ],
      },
      { label: "คลานต่อไปตามทาง", outcomes: [{ chance: 1, text: "คุณไม่สนใจและคลานจากไป" }] },
    ],
  },
  {
    id: "apple_snail_eggs",
    archetypes: MO,
    stages: ["adult"],
    requires: { subgroups: SNAIL, families: ["Ampullariidae"], flags: ["has_mate"] },
    weight: 30,
    text: "ตอนกลางคืน คุณปีนขึ้นต้นข้าวจนพ้นผิวน้ำ",
    fact: "หอยเชอรี่เป็นสัตว์ต่างถิ่นที่ระบาดในนาข้าวของไทย มันวางไข่สีชมพูสดไว้เหนือน้ำเพื่อให้พ้นจากปลาที่กินไข่",
    choices: [
      { label: "วางไข่ให้สูงพ้นน้ำ", outcomes: [{ chance: 1, text: "คุณวางไข่สีชมพูสด {n} ฟองเกาะเป็นพวงบนต้นข้าว", effects: { offspring: "clutch", fullness: -15, removeFlags: ["has_mate"], addFlags: ["parent"] } }] },
    ],
  },
  {
    id: "apple_snail_ducks",
    archetypes: MO,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: SNAIL, families: ["Ampullariidae"] },
    weight: 3,
    text: "ชาวนาต้อนฝูงเป็ดลงมาในแปลงนาที่คุณอยู่",
    choices: [
      {
        label: "มุดลงโคลน",
        outcomes: [
          { chance: 0.7, text: "เป็ดไซ้หาไปทั่ว แต่ไม่เจอคุณที่ฝังอยู่ใต้โคลน", effects: { safety: 5 } },
          { chance: 0.3, text: "ปากเป็ดคุ้ยลงมาถึงตัวคุณ", effects: { death: "human" } },
        ],
      },
      {
        label: "ปีนขึ้นต้นข้าว",
        outcomes: [
          { chance: 0.5, text: "เป็ดไม่ได้มองขึ้นข้างบน", effects: { safety: 5 } },
          { chance: 0.5, text: "ชาวนาเดินเก็บหอยตามต้นข้าวใส่ถุง", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "cone_snail_harpoon",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: SNAIL, families: ["Conidae"] },
    weight: 6,
    text: "ปลาตัวเล็กตัวหนึ่งนอนพักอยู่บนพื้นทรายใกล้ๆ",
    fact: "หอยเต้าปูนยิงฟันที่มีรูปร่างคล้ายฉมวกพร้อมพิษใส่เหยื่อ พิษของบางชนิดร้ายแรงถึงชีวิตมนุษย์",
    choices: [
      {
        label: "ยิงฉมวกพิษ",
        outcomes: [
          { chance: 0.8, text: "พิษทำให้ปลาขยับไม่ได้ในทันที คุณค่อยๆ กลืนมันลงไป", effects: { fullness: 40 } },
          { chance: 0.2, text: "ฉมวกพลาดเป้า ปลาว่ายหนีไป", effects: { fullness: -5 } },
        ],
      },
      { label: "รอให้มันว่ายเข้ามาใกล้กว่านี้", outcomes: [{ chance: 1, text: "มันว่ายมาหยุดตรงหน้าคุณพอดี", effects: { fullness: 30, safety: 5 } }] },
    ],
  },

  // ---------- หอยสองฝาและหอยอื่นๆ ----------
  {
    id: "mollusc_larva_drift",
    archetypes: MO,
    stages: ["larva"],
    weight: 8,
    text: "ขนเล็กๆ รอบตัวพาคุณหมุนติ้วไปในน้ำ ท่ามกลางแพลงก์ตอนนับล้าน",
    choices: [
      { label: "ว่ายขึ้นหาแสง", outcomes: [{ chance: 1, text: "ผิวน้ำมีแพลงก์ตอนพืชให้กินเยอะ", effects: { fullness: 25 } }] },
      {
        label: "ปล่อยตัวลอยตามกระแส",
        outcomes: [
          { chance: 0.7, text: "กระแสน้ำพาคุณไปใกล้ชายฝั่งที่เหมาะจะลงเกาะ", effects: { move: "near", safety: 10 } },
          { chance: 0.3, text: "แมงกะพรุนหวีลอยผ่านมา กวาดตัวอ่อนรอบตัวคุณไปหมด", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "bivalve_settle",
    opening: true,
    archetypes: MO,
    stages: ["settle"],
    requires: { subgroups: BIVALVE },
    weight: 10,
    text: "เท้าเล็กๆ ของคุณงอกออกมา ถึงเวลาเลือกบ้านที่จะอยู่ไปตลอดชีวิต",
    fact: "ตัวอ่อนหอยหลายชนิดเลือกที่ลงเกาะจากสารเคมีที่หอยตัวเต็มวัยชนิดเดียวกันปล่อยออกมา",
    choices: [
      {
        label: "โขดหินที่มีหอยตัวอื่นเกาะอยู่แล้ว",
        outcomes: [
          { chance: 0.8, text: "ที่ที่หอยตัวอื่นอยู่รอดได้ แปลว่าปลอดภัย", effects: { safety: 10 } },
          { chance: 0.2, text: "ที่นี่แน่นมาก คุณต้องแย่งอาหารกับเพื่อนบ้าน", effects: { fullness: -10 } },
        ],
      },
      { label: "พื้นโคลนนิ่มในป่าชายเลน", outcomes: [{ chance: 1, text: "คุณฝังตัวลงในโคลนที่อุดมไปด้วยอาหาร", effects: { fullness: 15, safety: 5 } }] },
      {
        label: "ท้องเรือที่ลอยผ่านมา",
        outcomes: [
          { chance: 0.5, text: "คุณเดินทางไปไกลถึงท่าเรืออีกฝั่งของทะเล", effects: { move: "far", fullness: 10 } },
          { chance: 0.5, text: "ลูกเรือขูดท้องเรือ คุณหลุดร่วงลงไปในน้ำ", effects: { health: -25, cause: "human" } },
        ],
      },
    ],
  },
  {
    id: "bivalve_filter",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: BIVALVE },
    weight: 8,
    text: "น้ำขึ้นพาแพลงก์ตอนมาเต็มไปหมด",
    fact: "หอยนางรมตัวโตเต็มวัยกรองน้ำได้หลายสิบลิตรต่อวัน ช่วยให้น้ำทะเลใสขึ้น",
    choices: [
      {
        label: "เปิดเปลือกกว้างกรองอาหาร",
        outcomes: [
          { chance: 0.85, text: "เหงือกของคุณกรองแพลงก์ตอนเข้าปากไม่หยุด", effects: { fullness: 35 } },
          { chance: 0.15, text: "ปูตัวหนึ่งเอาก้ามงัดเข้ามาตอนเปลือกเปิดอยู่", effects: { fullness: 15, health: -20, cause: "predator" } },
        ],
      },
      { label: "แง้มเปลือกแค่นิดเดียว", outcomes: [{ chance: 1, text: "ได้อาหารน้อยลงแต่ปลอดภัยกว่า", effects: { fullness: 20, safety: 5 } }] },
    ],
  },
  {
    id: "bivalve_starfish",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: BIVALVE, habitat: ["sea"] },
    weight: 4,
    text: "ดาวทะเลตัวหนึ่งค่อยๆ คลานขึ้นมาคร่อมเปลือกของคุณ",
    choices: [
      {
        label: "ปิดเปลือกให้แน่นที่สุด",
        outcomes: [
          { chance: 0.6, text: "ดาวทะเลดึงอยู่หลายชั่วโมง สุดท้ายก็ยอมแพ้", effects: { health: -5, fullness: -10 } },
          { chance: 0.4, text: "กล้ามเนื้อของคุณล้าจนเปลือกแง้มออกนิดเดียว ดาวทะเลก็ดันกระเพาะเข้ามา", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "bivalve_pearl",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: BIVALVE },
    weight: 3,
    text: "ปรสิตตัวจิ๋วชอนไชเข้ามาในเนื้อเยื่อใต้เปลือกของคุณ",
    fact: "ไข่มุกเกิดเมื่อหอยหลั่งสารมุกเคลือบสิ่งแปลกปลอมทีละชั้น ไข่มุกธรรมชาติส่วนใหญ่เกิดจากปรสิตมากกว่าเม็ดทราย",
    choices: [
      { label: "เคลือบมันด้วยสารมุก", outcomes: [{ chance: 1, text: "ชั้นมุกห่อสิ่งแปลกปลอมไว้ หลายปีต่อมามันกลายเป็นไข่มุกเม็ดเล็ก", effects: { health: 5 } }] },
      { label: "ปล่อยไว้", outcomes: [{ chance: 1, text: "ปรสิตกัดกินเนื้อเยื่อไปเรื่อยๆ", effects: { health: -15, cause: "disease" } }] },
    ],
  },
  {
    id: "bivalve_harvest",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: BIVALVE },
    weight: 2,
    threatenedWeight: 2,
    text: "น้ำลงต่ำสุดในรอบเดือน มีคนเดินถือตะกร้าลงมาบนหาดโคลน",
    choices: [
      {
        label: "ปิดเปลือกให้แน่น",
        outcomes: [
          { chance: 0.6, text: "เขาเดินผ่านคุณไป", effects: { safety: 5 } },
          { chance: 0.4, text: "มือคู่หนึ่งหยิบคุณใส่ตะกร้า", effects: { death: "human" } },
        ],
      },
      {
        label: "มุดลงโคลนให้ลึกขึ้น",
        outcomes: [
          { chance: 0.8, text: "คุณฝังตัวลึกพ้นมือคน", effects: { safety: 5, fullness: -5 } },
          { chance: 0.2, text: "คราดเหล็กคุ้ยลงมาถึงตัวคุณ", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "bivalve_redtide",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: BIVALVE },
    weight: 2,
    text: "น้ำรอบตัวเปลี่ยนเป็นสีแดงคล้ำ และมีกลิ่นแปลกๆ",
    fact: "แพลงก์ตอนบลูมหรือขี้ปลาวาฬทำให้ออกซิเจนในน้ำลดลง และแพลงก์ตอนบางชนิดสร้างสารพิษที่สะสมในหอย",
    choices: [
      { label: "ปิดเปลือกรอให้ผ่านไป", outcomes: [{ chance: 1, text: "คุณอดอาหารหลายวันจนน้ำกลับมาใส", effects: { fullness: -15, health: -5 } }] },
      { label: "กรองกินตามปกติ", outcomes: [{ chance: 1, text: "อาหารเยอะก็จริง แต่สารพิษสะสมในตัวคุณ", effects: { fullness: 30, health: -20, cause: "disease" } }] },
    ],
  },
  {
    id: "giant_clam_sun",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: BIVALVE, families: ["Tridacnidae"] },
    weight: 7,
    text: "แสงแดดยามสายส่องลงมาถึงแนวปะการังที่คุณฝังตัวอยู่",
    fact: "หอยมือเสือเลี้ยงสาหร่ายเซลล์เดียวไว้ในเนื้อเยื่อ สาหร่ายสร้างอาหารจากแสงแดดแล้วแบ่งให้หอย",
    choices: [
      { label: "กางเนื้อเยื่อหลากสีรับแดด", outcomes: [{ chance: 1, text: "สาหร่ายในเนื้อเยื่อของคุณสังเคราะห์แสงสร้างอาหารให้ทั้งวัน", effects: { fullness: 35 } }] },
      { label: "หุบเปลือกเมื่อมีเงาผ่าน", outcomes: [{ chance: 1, text: "ปลาที่ว่ายผ่านจิกไม่โดนเนื้อเยื่อของคุณ", effects: { fullness: 15, safety: 10 } }] },
    ],
  },
  {
    id: "chiton_grip",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: ["mollusc.other"] },
    weight: 7,
    text: "คลื่นลูกใหญ่ซัดเข้าโขดหินที่คุณเกาะอยู่",
    fact: "ลิ่นทะเล (chiton) มีเปลือกแปดแผ่นเรียงต่อกัน จึงม้วนตัวกลมได้เมื่อหลุดจากหิน",
    choices: [
      { label: "ยึดหินแน่นด้วยเท้าแบนกว้าง", outcomes: [{ chance: 1, text: "คลื่นซัดผ่านไปโดยคุณไม่ขยับเลย", effects: { safety: 10 } }] },
      {
        label: "ปล่อยตัวแล้วม้วนเป็นวง",
        outcomes: [
          { chance: 0.6, text: "คลื่นพาคุณไปตกบนหินก้อนใหม่ที่มีตะไคร่เต็มไปหมด", effects: { fullness: 20 } },
          { chance: 0.4, text: "คุณกลิ้งไปกระแทกหินแหลม", effects: { health: -20, cause: "accident" } },
        ],
      },
    ],
  },
  {
    id: "chiton_graze",
    archetypes: MO,
    stages: ["adult", "elder"],
    requires: { subgroups: ["mollusc.other"] },
    weight: 6,
    text: "น้ำลงแล้ว ตะไคร่บนโขดหินเปิดโล่งให้เห็น",
    fact: "ฟันของลิ่นทะเลเคลือบด้วยแร่เหล็ก จึงแข็งพอจะขูดตะไคร่ออกจากหินได้",
    choices: [
      { label: "ขูดกินตอนกลางคืน", outcomes: [{ chance: 1, text: "ความมืดช่วยซ่อนคุณจากนก คุณกินได้เต็มที่", effects: { fullness: 30, safety: 5 } }] },
      {
        label: "ขูดกินเลยตอนนี้",
        outcomes: [
          { chance: 0.6, text: "ตะไคร่สดๆ อร่อยมาก", effects: { fullness: 35 } },
          { chance: 0.4, text: "นกชายเลนเดินมาเห็นคุณ", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
];
